import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './UserProfile.css'; // Reuse the UserProfile styling
import { useTranslation } from 'react-i18next';

const MyBookings = () => {
  const { currentUser, getUserBookings } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('upcoming'); // Default to upcoming
  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Add UTF-8 meta tag to ensure proper encoding
  useEffect(() => {
    // Check if the charset meta tag already exists
    let charsetMeta = document.querySelector('meta[charset]');
    if (!charsetMeta) {
      // Create and append the charset meta tag if it doesn't exist
      charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      document.head.insertBefore(charsetMeta, document.head.firstChild);
    } else {
      // Ensure it's set to UTF-8
      charsetMeta.setAttribute('charset', 'utf-8');
    }
    
    // Also ensure Content-Type meta is present
    let contentTypeMeta = document.querySelector('meta[http-equiv="Content-Type"]');
    if (!contentTypeMeta) {
      contentTypeMeta = document.createElement('meta');
      contentTypeMeta.setAttribute('http-equiv', 'Content-Type');
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
      document.head.appendChild(contentTypeMeta);
    } else {
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
    }
  }, []);

  // Scroll to top when coming from profile page
  useEffect(() => {
    if (location.state?.scrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Fetch user bookings
  useEffect(() => {
    const fetchBookings = async () => {
      if (!currentUser) return;
      try {
        setLoading(true);
        setError('');
        const bookingsData = await getUserBookings();
        setBookings(bookingsData || []);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError(t('failed_load_bookings'));
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [currentUser, getUserBookings, t]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('date_locale'), {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC' // Treat the date as UTC to fix +2h issue
    });
  };

  // Check if a booking is in the future
  const isUpcoming = (dateString) => {
    const now = new Date();
    const bookingDate = new Date(dateString);
    return bookingDate > now;
  };

  // Filter bookings based on selected filter
  const getFilteredBookings = () => {
    if (filter === 'upcoming') {
      return bookings.filter(booking => 
        isUpcoming(booking.start_time) && booking.status.toLowerCase() === 'confirmed'
      );
    } else if (filter === 'past') {
      return bookings.filter(booking => 
        !isUpcoming(booking.start_time) && booking.status.toLowerCase() === 'confirmed'
      );
    } else if (filter === 'cancelled') {
      return bookings.filter(booking => booking.status.toLowerCase() === 'cancelled');
    }
    return bookings;
  };

  // Get the correct icon for booking status
  const getStatusIcon = (status) => {
    switch(status.toLowerCase()) {
      case 'confirmed': return '✅';
      case 'cancelled': return '❌';
      case 'pending': return '⏳';
      default: return '🔄';
    }
  };

  // Handle booking cancellation
  const handleCancelBooking = async (bookingId) => {
    setError('');                           // clear any previous error
    setCancellingBookingId(bookingId);
    try {
      const resp = await axios.post(
        `/bookings/${bookingId}/cancel`
      );

      // on success, update status locally
      setBookings(prev =>
        prev.map(b =>
          b.booking_id === bookingId
            ? { ...b, status: 'cancelled' }
            : b
        )
      );
    } catch (err) {
      console.error('Error cancelling booking:', err);

      // try to parse our structured JSON error:
      const apiError = err.response?.data?.error;
      const msg = apiError?.message 
        || err.response?.data?.message 
        || t('failed_cancel_booking');
      setError(msg);
    } finally {
      setCancellingBookingId(null);
    }
  };

  // Navigate back to profile
  const handleBackToProfile = () => {
    navigate('/profile');
  };

  const filteredBookings = getFilteredBookings();

  if (!currentUser) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>{t('loading_user_data')}</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="back-button-wrapper">
          <button 
            className="back-button" 
            onClick={handleBackToProfile}
            aria-label={t('back_to_profile')}
          >
            {t('back_to_profile')}
          </button>
          <h1>{t('my_bookings')}</h1>
        </div>
        <div className="booking-filter-container">
          <span className="filter-label">{t('show')}</span>
          <div className="booking-filters">
            <button 
              className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`} 
              onClick={() => setFilter('upcoming')}
            >
              {t('upcoming')}
            </button>
            <button 
              className={`filter-btn ${filter === 'past' ? 'active' : ''}`} 
              onClick={() => setFilter('past')}
            >
              {t('past')}
            </button>
            <button 
              className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`} 
              onClick={() => setFilter('cancelled')}
            >
              {t('cancelled')}
            </button>
          </div>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="profile-content bookings-content">
        <div className="profile-section full-width">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('loading_your_bookings')}</p>
            </div>
          ) : filteredBookings.length > 0 ? (
            <div className="bookings-list compact">
              {filteredBookings.map(booking => (
                <div 
                  key={booking.booking_id} 
                  className={`booking-card compact ${
                    booking.status.toLowerCase() === 'cancelled' 
                      ? 'cancelled' 
                      : isUpcoming(booking.start_time) ? 'upcoming' : 'past'
                  }`}
                >
                  <div className="booking-header">
                    <h3>{booking.class_name}</h3>
                    <span className={`booking-status status-${booking.status.toLowerCase()}`}>
                      {getStatusIcon(booking.status)} {booking.status}
                    </span>
                  </div>
                  
                  <div className="booking-details">
                    <div className="booking-details-row">
                      <p>
                        <strong>{t('date_time')}:</strong>
                        <span className="detail-value">{formatDate(booking.start_time)}</span>
                      </p>
                      <p>
                        <strong>{t('location')}:</strong>
                        <span className="detail-value">{booking.location}</span>
                      </p>
                    </div>
                    <div className="booking-details-row">
                      <p>
                        <strong>{t('instructor')}:</strong>
                        <span className="detail-value">{booking.instructor_name}</span>
                      </p>
                      <p>
                        <strong>{t('duration')}:</strong>
                        <span className="detail-value">{booking.duration} {t('min')}</span>
                      </p>
                    </div>
                  </div>
                  
                  {isUpcoming(booking.start_time) && booking.status.toLowerCase() === 'confirmed' && (
                    <div className="booking-actions">
                      <button 
                        className="cancel-booking-btn"
                        onClick={() => handleCancelBooking(booking.booking_id)}
                        disabled={cancellingBookingId === booking.booking_id}
                      >
                        {cancellingBookingId === booking.booking_id 
                          ? t('cancelling')
                          : t('cancel_booking')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-bookings">
              <div className="no-data-icon">🧘‍♀️</div>
              <p>
                {filter === 'upcoming'
                  ? t('no_upcoming_bookings')
                  : filter === 'past'
                    ? t('no_past_bookings')
                    : t('no_cancelled_bookings')
                }
              </p>
              {filter === 'upcoming' && (
                <>
                  <p>{t('book_class_message')}</p>
                  <button 
                    className="view-schedule-btn" 
                    onClick={() => navigate('/schedule')}
                  >
                    {t('view_schedule')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBookings;
